/* DEVELOPER NOTE:
	Beast B2 migration port-forward (Select2 -> pict-section-picker): the "MaterialsSampleSelector"
	EDIT theme now renders via the Picker stack, attaching MaterialsSamplePicker — the cascade subclass
	carrying the Sample hub logic (creation, DocumentPolyJoin, tracking family, save stamp+projection).
	The MAIN Edit template is BESPOKE (not a bare reference): it reproduces the Picker Edit chrome and
	adds the legacy Sample Search Panel addon button (losing it would orphan the panel). The tabular +
	vertical variants reference the shared Picker templates (which carry the tracking-GUID slot).
	READ / PRINT now render via MaterialsSamplePickerDisplay (B5 display port: __Record-first GUID resolve + the QR-code image prepend).
*/
const _TemplatesGroupedByTheme =
{
	/* EDIT THEME - Interactive mode for browsers */
	Edit: [
		{
			HashPostfix: `-Template-Input-InputType-MaterialsSampleSelector`,
			DefaultInputExtensions: [ 'MaterialsSamplePicker' ],
			Template: /*HTML*/`
			<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker (+ sample panel addon) -->
			<div class="column {~ColumnWidth:Record~}">
				<input
					id="MATERIALS-ENTITY-TRACKING-GUID-{~D:Record.Macro.RawHTMLID~}"
					type="hidden"
					tabindex="-1"
					value=""
				/>
				<input
					id="{~D:Record.Macro.RawHTMLID~}"
					type="hidden"
					tabindex="-1"
					{~D:Record.Macro.InputFullProperties~}
					{~D:Record.Macro.InputChangeHandler~}
					{~Required:Record~}
					data-validation-surface="PICKER-FOR-{~D:Record.Macro.RawHTMLID~}"
					value=""
				/>
				<div class="field">
					<a id="PICKER-FILTER-TOGGLE-{~D:Record.Macro.RawHTMLID~}"
						class="button is-ghost is-pulled-right p-0 is-size-7 {~FilterToggleHidden:Record~} {~FilterToggleDisabled:Record~}"
						title="Show all (ignore filters)"
						onclick="_Pict.providers.MaterialsSamplePicker.toggleFilterOverride('{~D:Record.Macro.RawHTMLID~}', '{~D:Record.DataAddress~}')">
						<span class="icon"><i class="fas fa-toggle-off"></i></span><span class="icon-text">Show All</span>
					</a>
					<label class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
					<div class="field has-addons has-addons-right mb-0">
						<p class="control control-status is-expanded">
							<span class="control-status-icon"></span>
							<span id="PICKER-FOR-{~D:Record.Macro.RawHTMLID~}" class="pps-form-host" {~D:Record.Macro.ControlAttr~}></span>
						</p>
						<p id="SAMPLE-LOCK-CONTROL-{~D:Record.Macro.RawHTMLID~}" class="control is-hidden">
							<button id="SAMPLE-LOCK-{~D:Record.Macro.RawHTMLID~}" class="button" type="button" aria-pressed="false" onclick="_Pict.providers.MaterialsSamplePicker.toggleSampleLock('{~D:Record.Macro.RawHTMLID~}')">
								<span class="icon is-small">
									<i class="fas fa-lock-open"></i>
								</span>
							</button>
						</p>
						<p class="control">
							<button id="SAMPLE-PANEL-BUTTON-{~D:Record.Macro.RawHTMLID~}" class="button is-link" type="button" {~Disabled:Record~} onclick="_Pict.views.HeadlightPaneSamples.openSamplePanelFromAdvancedButton(event)">
								<span class="icon is-small">
									<i class="fas fa-bars-filter"></i>
								</span>
							</button>
						</p>
					</div>
					<p class="help"><span class="description-text">{~D:Record.Description~}</span></p>
				</div>
			</div>`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-MaterialsSampleSelector',
			DefaultInputExtensions: [ 'MaterialsSamplePicker' ],
			Template: /*HTML*/`
				<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker -->
				{~T:PictProvider-Theme-Edit-VerticalTemplate-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-MaterialsSampleSelector',
			DefaultInputExtensions: [ 'MaterialsSamplePicker' ],
			Template: /*HTML-Partial*/`
				<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker -->
				{~T:PictProvider-Theme-Edit-TabularTemplate-Begin-Input-InputType-Picker~}`,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-MaterialsSampleSelector',
			DefaultInputExtensions: [ 'MaterialsSamplePicker' ],
			Template: /*HTML-Partial*/`
				{~T:PictProvider-Theme-Edit-TabularTemplate-End-Input-InputType-Picker~}`,
		},
	],
	/* READ THEME - Interactive mode for browsers in a locked/disabled/readonly state.
	   B5 display port: rendered by the Picker stack's MaterialsSamplePickerDisplay (PICKER-DISPLAY-* nodes) instead of
	   the legacy Materials*Display — resolve is __Record-first, then identity-aware. */
	Read: [
		{
			HashPostfix: `-Template-Input-InputType-MaterialsSampleSelector`,
			DefaultInputExtensions: [ 'MaterialsSamplePickerDisplay' ],
			Template: /*HTML*/`
			<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker (read display) -->
			<input
				id="MATERIALS-ENTITY-TRACKING-GUID-{~D:Record.Macro.RawHTMLID~}"
				type="hidden"
				tabindex="-1"
				value=""
			/>
			<div class="column {~ColumnWidth:Record~}">
				<div class="field">
					<label {~D:Record.Macro.HTMLForID~} class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
					<p class="control">
						<div id="PICKER-DISPLAY-FOR-{~D:Record.Macro.RawHTMLID~}" class="textarea" {~D:Record.Macro.ControlAttr~} {~D:Record.Macro.InputFullProperties~} disabled="disabled"></div>
					</p>
				</div>
			</div>
			`,
		},
		{
			HashPostfix: `-TabularTemplate-Begin-Input-InputType-MaterialsSampleSelector`,
			DefaultInputExtensions: [ 'MaterialsSamplePickerDisplay' ],
			Template: /*HTML-Partial*/`
			<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker (tabular read display) -->
			<input
				id="MATERIALS-ENTITY-TRACKING-GUID-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
				type="hidden"
				tabindex="-1"
				value=""
			/>
			<div class="field mb-0">
				<p class="control">
					<div
						id="PICKER-DISPLAY-TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
						class="textarea"
						{~D:Record.Macro.ControlAttr~}
						{~D:Record.Macro.HTMLName~}
						{~D:Record.Macro.InformaryTabular~}
						disabled="disabled" `,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-MaterialsSampleSelector',
			Template: /*HTML-Partial*/`
					></div>
				</p>
			</div>
			`,
		},
	],
	/* PRINT THEME - Non-interactive mode for PDF documents (same MaterialsSamplePickerDisplay resolve; PDF-static node). */
	Print: [
		{
			HashPostfix: `-Template-Input-InputType-MaterialsSampleSelector`,
			DefaultInputExtensions: [ 'MaterialsSamplePickerDisplay' ],
			Template: /*HTML*/`
			<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker (print display) -->
			<input
				id="MATERIALS-ENTITY-TRACKING-GUID-{~D:Record.Macro.RawHTMLID~}"
				type="hidden"
				tabindex="-1"
				value=""
			/>
			<div class="column {~ColumnWidth:Record~} {~HideInPDF:Record~}">
				<div class="field">
					<label {~D:Record.Macro.HTMLForID~} class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
					<div class="control">
						<div class="input is-static wrap-selections-for-print" id="PICKER-DISPLAY-FOR-{~D:Record.Macro.RawHTMLID~}" {~D:Record.Macro.ControlAttr~} {~D:Record.Macro.InputFullProperties~}></div>
					</div>
				</div>
			</div>
			`,
		},
		{
			HashPostfix: `-TabularTemplate-Begin-Input-InputType-MaterialsSampleSelector`,
			DefaultInputExtensions: [ 'MaterialsSamplePickerDisplay' ],
			Template: /*HTML-Partial*/`
			<!-- Provided InputType: MaterialsSampleSelector; Applied InputType: Picker (tabular print display) -->
			<input
				id="MATERIALS-ENTITY-TRACKING-GUID-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
				type="hidden"
				tabindex="-1"
				value=""
			/>
			<div
				id="PICKER-DISPLAY-TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}"
				class="input is-static wrap-selections-for-print"
				{~D:Record.Macro.ControlAttr~}
				{~D:Record.Macro.HTMLName~}
				{~D:Record.Macro.InformaryTabular~}
				`,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-MaterialsSampleSelector',
			Template: /*HTML-Partial*/`
			></div>
			`,
		},
	],
};

module.exports = _TemplatesGroupedByTheme;
